import { Controller, useFormContext } from 'react-hook-form';
import { Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { EstateInputDataFormatDataType, EstateInputDataInstance_FieldsFragment } from 'queries';

import FormDateField from 'common/form-date-field';
import FormPhoneField from 'common/form-phone-field';
import FormSelectField from 'common/form-select-field';
import FormSsnField from 'common/form-ssn-field';
import FormTextField from 'common/form-text-field';

import { AssignedTaskMatterInfoType } from './assigned-task-matter-info';

const useStyles = makeStyles(() => ({
  headerText: {
    display: 'flex',
    alignItems: 'center',
  },
  columnHeader: {
    width: '250px',
  },
  column: {
    display: 'flex',
    width: '100%',
    padding: '20px 0',
    borderBottom: '1px solid #ECEEF1',
    alignItems: 'center',
  },
  columnBody: {
    display: 'flex',
    flex: 1,
  },
  fieldRequired: {
    color: 'red',
  },
}));

type Props = {
  estateInputDataInstances: EstateInputDataInstance_FieldsFragment[];
};

const AssignedTaskMatterInfoRequiredInfoForm = ({ estateInputDataInstances }: Props) => {
  const classes = useStyles();

  const {
    formState: { errors },
    control,
  } = useFormContext<AssignedTaskMatterInfoType>();

  const renderEstateInputDataInstanceField = (
    estateInputDataInstance: EstateInputDataInstance_FieldsFragment,
    index: number,
  ) => {
    const isTextSingleLine =
      estateInputDataInstance.estateInputDataFormat.dataType ===
      EstateInputDataFormatDataType.TextSingleLine;

    const isTextMultiLine =
      estateInputDataInstance.estateInputDataFormat.dataType ===
      EstateInputDataFormatDataType.TextMultiLine;

    const isNumber =
      estateInputDataInstance.estateInputDataFormat.dataType ===
      EstateInputDataFormatDataType.Number;

    const isNumberNoDecimal =
      estateInputDataInstance.estateInputDataFormat.dataType ===
      EstateInputDataFormatDataType.NumberNoDecimal;

    switch (estateInputDataInstance.estateInputDataFormat.dataType) {
      case EstateInputDataFormatDataType.TextSingleLine:
      case EstateInputDataFormatDataType.TextMultiLine:
      case EstateInputDataFormatDataType.Number:
      case EstateInputDataFormatDataType.NumberNoDecimal:
      case EstateInputDataFormatDataType.List: // TODO - render list type once list infrastructure is implemented
      default:
        return (
          <Controller
            name={`infoEstateInputDataInstanceFormValues.${index}.data`}
            control={control}
            render={({ field: { ref: _, onChange, ...field } }) => (
              <FormTextField
                {...field}
                onChange={(evt) => {
                  const value = evt.target.value;
                  if (isNumber || isNumberNoDecimal) {
                    if (value && !isNaN(Number(value))) {
                      let numberValue = Number(value);

                      if (isNumberNoDecimal) {
                        numberValue = Math.trunc(numberValue);
                      }

                      onChange(numberValue.toString());
                    } else {
                      onChange(null);
                    }
                  } else {
                    onChange(value);
                  }
                }}
                inputProps={{
                  maxLength: isTextSingleLine || isTextMultiLine ? 80 : undefined,
                  type: isNumber || isNumberNoDecimal ? 'number' : undefined,
                  pattern: isNumberNoDecimal ? '[0-9]*' : undefined,
                }}
                helperText={estateInputDataInstance.estateInputDataFormat.labelHint}
                multiline={isTextMultiLine}
                errorMessage={
                  errors?.infoEstateInputDataInstanceFormValues &&
                  errors.infoEstateInputDataInstanceFormValues[index]?.data?.message
                }
              />
            )}
          />
        );

      case EstateInputDataFormatDataType.Date:
        return (
          <Controller
            name={`infoEstateInputDataInstanceFormValues.${index}.data`}
            control={control}
            render={({ field: { value, onChange, name } }) => (
              <FormDateField
                value={new Date(value || '')}
                onChange={(date) => {
                  onChange(date ? date.toISOString() : '');
                }}
                name={name}
                helperText={estateInputDataInstance.estateInputDataFormat.labelHint}
                errorMessage={
                  errors?.infoEstateInputDataInstanceFormValues &&
                  errors.infoEstateInputDataInstanceFormValues[index]?.data?.message
                }
              />
            )}
          />
        );

      case EstateInputDataFormatDataType.YesNo:
        return (
          <Controller
            name={`infoEstateInputDataInstanceFormValues.${index}.data`}
            control={control}
            render={({ field: { ref: _, onChange, ...field } }) => (
              <FormSelectField
                {...field}
                onChange={(evt) => {
                  onChange(evt.target.value);
                }}
                helperText={estateInputDataInstance.estateInputDataFormat.labelHint}
                options={[
                  {
                    value: '',
                    label: '',
                  },
                  {
                    value: 'true',
                    label: 'Yes',
                  },
                  {
                    value: 'false',
                    label: 'No',
                  },
                ]}
                errorMessage={
                  errors?.infoEstateInputDataInstanceFormValues &&
                  errors.infoEstateInputDataInstanceFormValues[index]?.data?.message
                }
              />
            )}
          />
        );
      case EstateInputDataFormatDataType.Ssn:
        return (
          <Controller
            name={`infoEstateInputDataInstanceFormValues.${index}.data`}
            control={control}
            render={({ field: { ref, ...field } }) => (
              <FormSsnField
                {...field}
                inputRef={ref}
                helperText={estateInputDataInstance.estateInputDataFormat.labelHint}
                errorMessage={
                  errors?.infoEstateInputDataInstanceFormValues &&
                  errors.infoEstateInputDataInstanceFormValues[index]?.data?.message
                }
              />
            )}
          />
        );

      case EstateInputDataFormatDataType.PhoneNumber:
        return (
          <Controller
            name={`infoEstateInputDataInstanceFormValues.${index}.data`}
            control={control}
            render={({ field: { name, onBlur, onChange, value } }) => (
              <FormPhoneField
                name={name}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                helperText={estateInputDataInstance.estateInputDataFormat.labelHint}
                errorMessage={
                  errors?.infoEstateInputDataInstanceFormValues &&
                  errors.infoEstateInputDataInstanceFormValues[index]?.message
                }
              />
            )}
          />
        );
    }
  };

  return (
    <>
      {estateInputDataInstances.map((estateInputDataInstance, index) => (
        <div className={classes.column}>
          <div className={classes.columnHeader}>
            <Typography className={classes.headerText}>
              <span className={classes.fieldRequired}>*</span>{' '}
              {estateInputDataInstance.estateInputDataFormat!.name}
            </Typography>
          </div>
          <div className={classes.columnBody}>
            {renderEstateInputDataInstanceField(estateInputDataInstance, index)}
          </div>
        </div>
      ))}
    </>
  );
};

export default AssignedTaskMatterInfoRequiredInfoForm;
